export type Point = { x: number; y: number };

export type Segment = [Point, Point];

export type Box = { x: number; y: number; width: number; height: number };

/** What an item looks like to the reader: the lines it is drawn with and any extra points, such as label centres. */
export type NavigationShape = {
  segments: Segment[];
  points: Point[];
};

export type ScoredCandidate<TItem> = {
  item: TItem;
  shape: NavigationShape;
  referencePoint: Point;
};

/** Candidates further off-axis than this ratio of their distance along the axis are ignored. */
const MAX_OFF_AXIS_RATIO = 1;
const OFF_AXIS_PENALTY = 2;
/** Scores closer than this are treated as a tie and decided by the distance to the reference point. */
const TIE_TOLERANCE = 5;

/** The side of the origin box facing the direction of travel, and its extent across that direction. */
const toOriginSide = (origin: Box, axis: Point) =>
  axis.x !== 0
    ? {
        side: axis.x > 0 ? origin.x + origin.width : origin.x,
        low: origin.y,
        high: origin.y + origin.height,
      }
    : {
        side: axis.y > 0 ? origin.y + origin.height : origin.y,
        low: origin.x,
        high: origin.x + origin.width,
      };

const alongAxis = (origin: Box, point: Point, axis: Point): number => {
  const { side } = toOriginSide(origin, axis);
  return axis.x !== 0 ? (point.x - side) * axis.x : (point.y - side) * axis.y;
};

const acrossAxis = (point: Point, axis: Point): number =>
  axis.x !== 0 ? point.y : point.x;

/** How far the point is outside the origin's extent across the direction of travel; 0 when straight ahead of it. */
const offAxis = (origin: Box, point: Point, axis: Point): number => {
  const { low, high } = toOriginSide(origin, axis);
  const across = acrossAxis(point, axis);
  return Math.max(low - across, 0, across - high);
};

const pointScore = (origin: Box, point: Point, axis: Point): number | null => {
  const along = alongAxis(origin, point, axis);
  const off = offAxis(origin, point, axis);
  if (along < 0 || off > along * MAX_OFF_AXIS_RATIO) return null;
  return along + off * OFF_AXIS_PENALTY;
};

const pointOnSegment = ([start, end]: Segment, t: number): Point => ({
  x: start.x + (end.x - start.x) * t,
  y: start.y + (end.y - start.y) * t,
});

/** Where along the segment a linear function crosses zero, if it does. */
const zeroCrossing = (atStart: number, atEnd: number): number[] =>
  atStart === atEnd ? [] : [atStart / (atStart - atEnd)];

/**
 * The score is linear along a segment except where the segment crosses the origin's facing side
 * or passes the edges of the origin's extent or of the allowed cone, so the best point is at one of those or at an end.
 */
const segmentScore = (
  origin: Box,
  segment: Segment,
  axis: Point
): number | null => {
  const [start, end] = segment;
  const { low, high } = toOriginSide(origin, axis);
  const acrossStart = acrossAxis(start, axis);
  const acrossEnd = acrossAxis(end, axis);
  const coneStart = alongAxis(origin, start, axis) * MAX_OFF_AXIS_RATIO;
  const coneEnd = alongAxis(origin, end, axis) * MAX_OFF_AXIS_RATIO;
  const scores = [
    0,
    1,
    ...zeroCrossing(
      alongAxis(origin, start, axis),
      alongAxis(origin, end, axis)
    ),
    ...zeroCrossing(acrossStart - low, acrossEnd - low),
    ...zeroCrossing(acrossStart - high, acrossEnd - high),
    ...zeroCrossing(low - acrossStart - coneStart, low - acrossEnd - coneEnd),
    ...zeroCrossing(acrossStart - high - coneStart, acrossEnd - high - coneEnd),
  ]
    .filter(t => t >= 0 && t <= 1)
    .map(t => pointScore(origin, pointOnSegment(segment, t), axis))
    .filter((score): score is number => score !== null);
  return scores.length > 0 ? Math.min(...scores) : null;
};

const shapeScore = (
  origin: Box,
  shape: NavigationShape,
  axis: Point
): number | null => {
  const scores = [
    ...shape.segments.map(segment => segmentScore(origin, segment, axis)),
    ...shape.points.map(point => pointScore(origin, point, axis)),
  ].filter((score): score is number => score !== null);
  return scores.length > 0 ? Math.min(...scores) : null;
};

const boxCenter = ({ x, y, width, height }: Box): Point => ({
  x: x + width / 2,
  y: y + height / 2,
});

export const boxSegments = ({ x, y, width, height }: Box): Segment[] => {
  const topLeft = { x, y };
  const topRight = { x: x + width, y };
  const bottomRight = { x: x + width, y: y + height };
  const bottomLeft = { x, y: y + height };
  return [
    [topLeft, topRight],
    [topRight, bottomRight],
    [bottomRight, bottomLeft],
    [bottomLeft, topLeft],
  ];
};

export const polylineSegments = (points: Point[]): Segment[] =>
  points.slice(1).map((end, index): Segment => [points[index], end]);

type Scored<TItem> = {
  item: TItem;
  score: number;
  referenceDistance: number;
};

const toScored = <TItem>(
  origin: Box,
  axis: Point,
  candidate: ScoredCandidate<TItem>
): Scored<TItem> | null => {
  const score = shapeScore(origin, candidate.shape, axis);
  if (score === null) return null;
  return {
    item: candidate.item,
    score,
    referenceDistance: Math.hypot(
      candidate.referencePoint.x - boxCenter(origin).x,
      candidate.referencePoint.y - boxCenter(origin).y
    ),
  };
};

/**
 * The candidate whose shape comes closest in the direction of `axis`, preferring items straight ahead.
 * Measured from the side of the origin box facing that direction; a point is a box without size.
 */
export const findNearestInDirection = <TItem>(
  origin: Box,
  axis: Point,
  candidates: ScoredCandidate<TItem>[]
): TItem | null => {
  const scored = candidates
    .map(candidate => toScored(origin, axis, candidate))
    .filter((entry): entry is Scored<TItem> => entry !== null);
  if (scored.length === 0) return null;
  const bestScore = Math.min(...scored.map(({ score }) => score));
  return scored
    .filter(({ score }) => score - bestScore < TIE_TOLERANCE)
    .reduce((nearest, entry) =>
      entry.referenceDistance < nearest.referenceDistance ? entry : nearest
    ).item;
};

/** The axis-aligned direction a vector mostly points in, or null for a zero vector. */
const dominantAxis = (dx: number, dy: number): Point | null => {
  if (dx === 0 && dy === 0) return null;
  return Math.abs(dx) >= Math.abs(dy)
    ? { x: Math.sign(dx), y: 0 }
    : { x: 0, y: Math.sign(dy) };
};

export const isAhead = (origin: Point, location: Point, axis: Point): boolean =>
  (location.x - origin.x) * axis.x + (location.y - origin.y) * axis.y > 0;

export const isSameDirection = (a: Point | null, b: Point): boolean =>
  a !== null && a.x === b.x && a.y === b.y;

const closestSegmentIndex = (segments: Segment[], point: Point): number =>
  segments
    .map(([start, end], index) => {
      const vx = end.x - start.x;
      const vy = end.y - start.y;
      const lengthSquared = vx * vx + vy * vy || 1;
      const t = Math.max(
        0,
        Math.min(
          1,
          ((point.x - start.x) * vx + (point.y - start.y) * vy) / lengthSquared
        )
      );
      return {
        index,
        distance: Math.hypot(
          start.x + vx * t - point.x,
          start.y + vy * t - point.y
        ),
      };
    })
    .reduce((closest, entry) =>
      entry.distance < closest.distance ? entry : closest
    ).index;

/** From a point on a line, usually its label, the way the line heads towards its start and towards its end. */
export const directionsAlongLine = (
  path: Point[],
  point: Point
): { towardStart: Point | null; towardEnd: Point | null } => {
  const segments = polylineSegments(path);
  if (segments.length === 0) return { towardStart: null, towardEnd: null };
  const [start, end] = segments[closestSegmentIndex(segments, point)];
  return {
    towardStart:
      dominantAxis(start.x - point.x, start.y - point.y) ??
      dominantAxis(start.x - end.x, start.y - end.y),
    towardEnd:
      dominantAxis(end.x - point.x, end.y - point.y) ??
      dominantAxis(end.x - start.x, end.y - start.y),
  };
};

/** The way a line leaves its first point. */
export const leavingDirection = (path: Point[]): Point | null =>
  path.length < 2
    ? null
    : dominantAxis(path[1].x - path[0].x, path[1].y - path[0].y);
